import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Title,
    Text,
    Alert,
    Table,
    Paper,
    Button,
    Group,
    ActionIcon,
    Tooltip,
} from '@mantine/core';
import { IconRefresh, IconTrash } from '@tabler/icons-react';
import { getUserSettingsService } from '../../services/UserSettingsService';
import type { UserSettingItem } from '../../services/UserSettingsService';
import { extractErrorMessage } from '../../utils/apiUtils';
import { TABLE_SCROLL_MIN_WIDTH } from '../../theme/mantineTheme';
import { LoadingState } from '../common/LoadingState';
import { EmptyState } from '../common/EmptyState';

const VALUE_MAX_DISPLAY = 80;

function truncate(value: string, maxLen: number = VALUE_MAX_DISPLAY): string {
    if (!value) return '—';
    return value.length <= maxLen ? value : value.slice(0, maxLen) + '…';
}

/** Format value for display; pretty-print JSON when applicable. */
function formatValueForTooltip(value: string): string {
    if (!value) return '—';
    const trimmed = value.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
            return JSON.stringify(JSON.parse(value), null, 2);
        } catch {
            return value;
        }
    }
    return value;
}

const UserSettingsTable: React.FC = () => {
    const [settings, setSettings] = useState<UserSettingItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [deletingKey, setDeletingKey] = useState<string | null>(null);

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const items = await getUserSettingsService().getAll();
            setSettings(items);
        } catch (err) {
            setError(extractErrorMessage(err) || 'Failed to fetch user settings');
            setSettings([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleDelete = useCallback(async (key: string) => {
        setDeletingKey(key);
        setError(null);
        try {
            await getUserSettingsService().delete(key);
            setSettings((prev) => prev.filter((item) => item.key !== key));
        } catch (err) {
            setError(extractErrorMessage(err) || 'Failed to delete setting');
        } finally {
            setDeletingKey(null);
        }
    }, []);

    return (
        <Box p="lg">
            <Group justify="space-between" align="center" mb="lg">
                <Title order={4} mb="xs">
                    User Settings
                </Title>
                <Button
                    variant="outline"
                    leftSection={<IconRefresh size={18} />}
                    onClick={fetchSettings}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Group>

            <Text size="sm" c="dimmed" mb="lg">
                GET api/scim/v2/mgmt/usersettings — view and delete stored settings (e.g. search_{'{entityName}'}).
            </Text>

            {error && (
                <Alert color="red" mb="md" withCloseButton onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <LoadingState variant="centered" />
            ) : (
                <Paper withBorder p="md">
                    {settings.length === 0 ? (
                        <EmptyState title="No user settings found" />
                    ) : (
                        <Table.ScrollContainer minWidth={TABLE_SCROLL_MIN_WIDTH}>
                            <Table withTableBorder stickyHeader>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th><strong>Key</strong></Table.Th>
                                        <Table.Th><strong>Value</strong></Table.Th>
                                        <Table.Th w={56} />
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {settings.map((item) => (
                                        <Table.Tr key={item.key}>
                                            <Table.Td>
                                                <Text size="sm" ff="monospace" fw={500}>
                                                    {item.key}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Tooltip label={formatValueForTooltip(item.value)} multiline maw={400}>
                                                    <Text size="sm" maw={400} truncate>
                                                        {truncate(item.value)}
                                                    </Text>
                                                </Tooltip>
                                            </Table.Td>
                                            <Table.Td>
                                                <Tooltip label="Delete this setting">
                                                    <ActionIcon
                                                        color="red"
                                                        variant="subtle"
                                                        aria-label={`Delete ${item.key}`}
                                                        onClick={() => handleDelete(item.key)}
                                                        disabled={deletingKey === item.key}
                                                        loading={deletingKey === item.key}
                                                    >
                                                        <IconTrash size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default UserSettingsTable;
